import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { fetchScopedAccounts, type ScopedAccountRow } from '@/api/orgAdmin';
import { filterParams, orderingParam } from './scopedAccountsQuery';
import { EMPTY_FILTERS } from './types';
import type { ScopedAccountsVM } from './useScopedAccounts';

/** Frame 35d's three radio options. */
export type ExportScope = 'page' | 'filtered' | 'branch';

/** Frame 35d's column checklist, in the order the frame lists them. */
export const EXPORT_COLUMNS = [
    'email',
    'sub',
    'org',
    'level',
    'roleKey',
    'status',
    'grantedAt',
    'syncStatus',
] as const;
export type ExportColumn = (typeof EXPORT_COLUMNS)[number];

const DEFAULT_COLUMNS: ExportColumn[] = ['email', 'sub', 'org', 'level', 'status', 'grantedAt'];

/** Same ceiling and same reason as `useScopedAccounts.MAX_SELECT_ALL_PAGES`. */
const MAX_EXPORT_PAGES = 20;
const SERVER_PAGE_SIZE = 100;

function csvCell(value: string): string {
    // RFC 4180: quote whenever the value could otherwise break a row, and double
    // any embedded quote. Excel-safe without a BOM dance because the Blob below
    // declares utf-8 explicitly.
    if (/[",\r\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
    return value;
}

/**
 * EAA-1839 (frame 35d, "Xuất CSV"): the export popover's engine.
 *
 * THIS IS A REAL EXPORT, not a disabled placeholder, and that is a change from
 * what this screen shipped before — the "Xuất CSV" button used to be permanently
 * inert with the reason "no CSV endpoint exists". No endpoint exists still; what
 * changed is the reading of the gap. There is nothing to ask the server for
 * because the client already has the only thing an export needs: the same rows the
 * table renders, from the same paginated endpoint, under the same filters. Walking
 * those pages and serialising them here produces a file whose contents provably
 * match the screen, which is more than a server-side exporter written against a
 * second query would guarantee.
 *
 * WHAT THE HONEST LIMITS ARE, and where each is surfaced:
 *  - The walk is capped at 20 × 100 = 2,000 rows (the endpoint's page ceiling is
 *    100 and there is no "give me every row" mode). Past that the file is a PREFIX,
 *    and `capped` says so in the popover before the download starts, not after.
 *  - "Toàn bộ nhánh, bỏ bộ lọc" clears the FILTERS only, never the scope — the
 *    scope org is a path segment, so "the whole branch" is the branch on screen.
 *  - XLSX is not offered. Frame 35d draws a CSV/XLSX toggle; writing a real
 *    workbook needs a library this app does not ship, and renaming a CSV to
 *    `.xlsx` produces a file Excel warns about on open. The toggle is rendered
 *    with CSV selected and XLSX inert with that reason.
 *  - There is no server-side audit row for an export, so the frame's "lượt xuất
 *    được ghi vào nhật ký định danh" promise is NOT repeated in the UI.
 */
export function useScopedAccountExport(vm: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState<ExportScope>('filtered');
    const [columns, setColumns] = useState<ExportColumn[]>(DEFAULT_COLUMNS);
    const [busy, setBusy] = useState(false);
    const [capped, setCapped] = useState(false);

    const maxRows = MAX_EXPORT_PAGES * SERVER_PAGE_SIZE;

    /** How many rows the chosen scope will actually write, as far as it is known. */
    const scopeCount = useMemo(() => {
        if (scope === 'page') return vm.rows.length;
        // "branch" drops the filters, so its true size is only known after the walk;
        // the filtered total is the closest honest lower bound to show beforehand.
        return vm.totalCount;
    }, [scope, vm.rows.length, vm.totalCount]);

    const willBeCapped = scope !== 'page' && scopeCount > maxRows;

    const toggleColumn = useCallback((column: ExportColumn) => {
        setColumns((prev) =>
            prev.includes(column) ? prev.filter((entry) => entry !== column) : [...prev, column]
        );
    }, []);

    const selectAllColumns = useCallback(() => setColumns([...EXPORT_COLUMNS]), []);

    const headerFor = useCallback(
        (column: ExportColumn) => t(`console.hierarchy.scopedAccounts.export.column_${column}`),
        [t]
    );

    const cellFor = useCallback(
        (row: ScopedAccountRow, column: ExportColumn): string => {
            switch (column) {
                case 'email':
                    return row.user_email || '';
                case 'sub':
                    return row.user_sub;
                case 'org':
                    return vm.orgNameById.get(row.organization_id) ?? row.organization_id;
                case 'level':
                    return row.level_name ?? '';
                case 'roleKey':
                    return row.role_key ?? '';
                case 'status':
                    return row.is_active
                        ? t('console.hierarchy.scopedAccounts.table.statusOk')
                        : t('console.hierarchy.scopedAccounts.table.statusRevoked');
                case 'grantedAt':
                    return row.granted_at;
                default:
                    return row.sync_status;
            }
        },
        [vm.orgNameById, t]
    );

    const collectRows = useCallback(async (): Promise<{ rows: ScopedAccountRow[]; capped: boolean }> => {
        if (scope === 'page') return { rows: vm.rows, capped: false };
        if (!vm.orgId || !accessToken) return { rows: [], capped: false };

        const effectiveFilters =
            scope === 'branch'
                ? { ...EMPTY_FILTERS, includeChildren: vm.filters.includeChildren }
                : vm.filters;

        const collected: ScopedAccountRow[] = [];
        let page = 1;
        let hitCap = false;
        for (;;) {
            const res = await fetchScopedAccounts(config.apiBase, accessToken, vm.orgId, {
                ...filterParams(effectiveFilters),
                page,
                pageSize: SERVER_PAGE_SIZE,
                ordering: orderingParam(vm.sortField, vm.sortDirection),
            });
            collected.push(...res.results);
            const reachedEnd = page >= res.total_pages;
            if (reachedEnd) break;
            if (page >= MAX_EXPORT_PAGES) {
                hitCap = true;
                break;
            }
            page += 1;
        }
        return { rows: collected, capped: hitCap };
    }, [scope, vm.rows, vm.orgId, vm.filters, vm.sortField, vm.sortDirection, accessToken, config.apiBase]);

    const run = useCallback(async () => {
        if (columns.length === 0) return;
        setBusy(true);
        setCapped(false);
        try {
            const { rows, capped: wasCapped } = await collectRows();
            setCapped(wasCapped);

            const lines = [
                columns.map((column) => csvCell(headerFor(column))).join(','),
                ...rows.map((row) => columns.map((column) => csvCell(cellFor(row, column))).join(',')),
            ];
            const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `tai-khoan-${(vm.orgName ?? vm.orgId ?? 'org').replace(/[^\w-]+/g, '-').toLowerCase()}.csv`;
            anchor.click();
            URL.revokeObjectURL(url);

            toast.success(
                t('console.hierarchy.scopedAccounts.export.doneToast', { count: rows.length })
            );
            if (!wasCapped) setOpen(false);
        } catch {
            toast.error(t('console.hierarchy.scopedAccounts.export.failedToast'));
        } finally {
            setBusy(false);
        }
    }, [columns, collectRows, headerFor, cellFor, vm.orgName, vm.orgId, toast, t]);

    return {
        open,
        setOpen,
        scope,
        setScope,
        columns,
        toggleColumn,
        selectAllColumns,
        headerFor,
        busy,
        capped,
        willBeCapped,
        maxRows,
        scopeCount,
        run,
    };
}

export type ScopedAccountExportVM = ReturnType<typeof useScopedAccountExport>;
